import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  COUNTRY_OPTIONS,
  getCountriesAsSelectOptions,
} from "../../../utils/constants/countryOptions";
import { useContextData } from "../../../hooks/context";
import { useCurrentLanguage } from "../../../hooks/useCurrentLanguage";
import { getXenoPrevalenceData } from "../../../services/dashboard";
import {
  getMonthlyXenophobicPrevalence,
  getYearlyXenophobicPrevalence,
} from "../../../services/singleMonths";
import { generateMultiplLineTooltip } from "../../../utils/functions/generateLineTooltip";
import { getDateString } from "../../../utils/functions/getDateString";
import { handleYearChange } from "../../../utils/functions/handleYearChange";
import { seriesValues } from "../../../utils/functions/seriesValues";
import { transformNameCountries } from "../../../utils/functions/transformNameCountries";
import {
  getTwoLineGraphOptions,
  LEGEND_CONFIG,
  TOOLTIP_STYLE,
} from "../../../utils/constants/graphConfigs";
import { xenoPrevalence as texts_es } from "../../../utils/text/graphTexts";
import { xenoPrevalence as texts_en } from "../../../utils/text/graphTexts_en";
import CenterSpin from "../../misc/spin";
import CardContainer from "../../reusable/CardContainer";
import CardOnlyYear from "../../reusable/CardOnlyYear";
import CardError from "../../reusable/CardError/CardError";
import miscText from "../../../utils/text/misc.json";
import { getGraphFileName } from "../../../utils/functions/getGraphFileName";
import { isCountryIncluded } from "../../../utils/functions/isCountryIncluded";
import { getDownloadSubtitle } from "../../../utils/functions/getDownloadSubtitle";

export default function XenoPrevalence({ selectedCountry, colorRBG }) {
  const { country } = useParams();
  const { dateOptions } = useContextData();
  const { language } = useCurrentLanguage();

  // State

  const [currentCountry, setCurrentCountry] = useState(
    selectedCountry ?? country
  );
  const [currentCardType] = useState("xeno-prevalence");
  const [texts, setTexts] = useState(language === "en" ? texts_en : texts_es);
  const [countryProps, setCountryProps] = useState(
    COUNTRY_OPTIONS.find(
      (opt) => opt.code === selectedCountry || opt.code === country
    )
  );
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorLoadingGraph, setErrorLoadingGraph] = useState(false);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [options, setOptions] = useState();
  const [selectedYear, setSelectedYear] = useState("HIST");
  const [lastMonthData, setLastMonthData] = useState();
  const [isMostRecentYear, setIsMostRecentYear] = useState(true);

  // Country Select
  const [countrySelectConfig, setCountrySelectConfig] = useState({
    isIncluded: currentCountry === "LAC",
    value: ["LAC"],
    options: getCountriesAsSelectOptions(language),
  });

  // Unique to Tweets component
  // Never send unique states to Card components
  const [currentCountryPrev, setCurrentCountryPrev] = useState();
  const [LACPRev, setLACPrev] = useState();

  const createDateString = () => {
    let dateString;

    try {
      dateString = isMostRecentYear
        ? getDateString(
            language,
            dateOptions.mostRecentDate.year,
            dateOptions.mostRecentDate.month
          )
        : getDateString(
            language,
            selectedYear === "HIST"
              ? dateOptions.mostRecentDate.year
              : selectedYear
          );
    } catch (error) {
      dateString = "-----";
    }

    return dateString;
  };

  function handleCountrySelectChange(newValue) {
    setCountrySelectConfig({
      ...countrySelectConfig,
      value: newValue,
    });
  }

  // Useeffect that collents yead and month data
  useEffect(() => {
    if (dateOptions) {
      setSelectedYear("HIST");
      setIsLoadingInitialData(false);
      setCountryProps(
        COUNTRY_OPTIONS.find((opt) => opt.code === selectedCountry)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions]);

  // useEffect to load data for graph and creates Echarts options
  // Listend to dateOptions and selected year
  // Only funs when these params are not null
  useEffect(() => {
    async function updateData() {
      try {
        setIsLoading(true);
        setOptions(null);

        const response = await getXenoPrevalenceData(
          currentCountry,
          selectedYear
        );

        if (response) {
          let transformedData = transformNameCountries(response.data, language);

          let countries = Object.keys(transformedData);
          countries.shift();

          let series = seriesValues(
            transformNameCountries(response.data, language)
          );

          if (country === "LAC") {
            countries = countries.filter((c) =>
              isCountryIncluded(c, countrySelectConfig.value, language)
            );

            series = series.filter((line) =>
              isCountryIncluded(line.name, countrySelectConfig.value, language)
            );
          }

          let options = {
            ...getTwoLineGraphOptions(
              transformedData.dates,
              "{value}%",
              countries,
              colorRBG,
              language
            ),
            legend: {
              ...LEGEND_CONFIG,
              data: countries,
              selectMode: currentCountry === "LAC" ? false : true,
              selected: (function () {
                let noSelected = {};

                if (currentCountry === "LAC") {
                  countries.forEach((e, i) => {
                    if (i === 0) {
                      noSelected[e] = true;
                    } else {
                      noSelected[e] = currentCountry === "LAC" ? true : false;
                    }
                  });
                } else {
                  noSelected["LAC"] = true;
                  noSelected[currentCountry] = true;
                }

                return noSelected;
              })(),
            },
            series: series,
            tooltip: {
              trigger: "axis",
              ...TOOLTIP_STYLE,
              formatter: function (params) {
                const isPercentage = true;
                const areColorsSwitched = true;
                return generateMultiplLineTooltip(
                  params,
                  transformedData,
                  isPercentage,
                  areColorsSwitched,
                  language
                );
              },
            },
          };
          setOptions(options);
          setIsDataAvailable(true);
          setIsLoading(false);
          setErrorLoadingGraph(false);
        }
      } catch (error) {
        setErrorLoadingGraph(true);
        setOptions(null);
        setIsDataAvailable(false);
        setIsLoading(false);
      }
    }

    if (!isLoadingInitialData && dateOptions && selectedYear) updateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentCountry,
    selectedYear,
    isLoadingInitialData,
    currentCountry,
    countrySelectConfig,
  ]);

  // useEffect to load data for description dynamic data
  useEffect(() => {
    const getData = async () => {
      let promises, callback;

      if (isMostRecentYear) {
        const currentMonthPromise = getMonthlyXenophobicPrevalence(
          currentCountry,
          dateOptions.mostRecentDate.year,
          dateOptions.mostRecentDate.month
        );

        promises = [currentMonthPromise];
        callback = (results) => {
          setLastMonthData(results[0].data.value);
          setCurrentCountryPrev(results[0].data.value);
          setLACPrev(results[0].data.lac_value);
        };
      } else {
        const currentYearPromise = getYearlyXenophobicPrevalence(
          currentCountry,
          selectedYear
        );
        const currentYearLACPromise = getYearlyXenophobicPrevalence(
          "LAC",
          selectedYear
        );

        promises = [currentYearPromise, currentYearLACPromise];
        callback = (results) => {
          const xenoAvgCurrentCountry = results[0].data.value;
          const xenoAvgLAC = results[1].data.value;

          setLastMonthData(xenoAvgCurrentCountry.toFixed(1));
          setCurrentCountryPrev(xenoAvgCurrentCountry.toFixed(1));
          setLACPrev(xenoAvgLAC.toFixed(1));
        };
      }
      Promise.all(promises)
        .then(callback)
        .catch((error) => {
          setLastMonthData("---");
          setCurrentCountryPrev("---");
          setLACPrev("---");
        });
    };

    if (!isLoadingInitialData && dateOptions) getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions, isLoadingInitialData, selectedYear, currentCountry]);

  useEffect(() => {
    if (selectedCountry) {
      setCurrentCountry(selectedCountry);
      setCountrySelectConfig({
        ...countrySelectConfig,
        isIncluded: selectedCountry === "LAC",
      });
      setCountryProps(
        COUNTRY_OPTIONS.find((opt) => opt.code === selectedCountry)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  useEffect(() => {
    setTexts(language === "en" ? texts_en : texts_es);
    setCountrySelectConfig({
      ...countrySelectConfig,
      options: getCountriesAsSelectOptions(language),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  function XenoPrevContent() {
    let content;
    try {
      content = (
        <CardOnlyYear
          countryProps={countryProps}
          dateOptions={dateOptions}
          isLoading={isLoading}
          errorLoading={errorLoadingGraph}
          isDataAvailable={isDataAvailable}
          options={options}
          selectedYear={selectedYear}
          lastMonthData={lastMonthData ? lastMonthData + "%" : "--"}
          dataDate={createDateString()}
          cardTitle={texts.boxTitle}
          methodology={texts.methodology()}
          dataSubtitle={texts.dataSubtitle}
          description={texts.description(
            isMostRecentYear,
            isMostRecentYear ? dateOptions.mostRecentDate.month : selectedYear,
            currentCountryPrev,
            LACPRev,
            currentCountry === "LAC"
          )}
          selectArray={[
            {
              selectedValue: selectedYear,
              changeHandler: (newSelectedYear) =>
                handleYearChange(
                  newSelectedYear,
                  setIsMostRecentYear,
                  setSelectedYear,
                  dateOptions
                ),
              options: dateOptions.yearOptions,
              placeHolderText: miscText["yearPlaceholder"][language],
              labelAttributeName: "years_list",
              valueAttributeName: "years_code",
            },
          ]}
          language={language}
          includesCountrySelect={countrySelectConfig.isIncluded}
          handleCountrySelectChange={handleCountrySelectChange}
          countrySelectDefaultValue={countrySelectConfig.value}
          countrySelectOptions={countrySelectConfig.options}
          downloadableCardID={currentCardType}
          downloadableCardFileName={getGraphFileName(
            language === "es"
              ? "PrevalenciaDeXenofobia"
              : "XenophobicPrevalence",
            currentCountry,
            selectedYear
          )}
          downloadableCardTitle={texts.boxTitle}
          downloadableCardSubtitle={getDownloadSubtitle(
            countryProps,
            selectedYear,
            language
          )}
          downloadableCardContainsLegend={true}
        />
      );
    } catch (error) {
      content = (
        <CardError
          cardTitle={texts.boxTitle}
          methodology={texts.methodology()}
          dataSubtitle={texts.dataSubtitle}
          description={texts.description()}
          selectArray={[
            {
              placeHolderText: miscText["yearPlaceholder"][language],
              language: language,
            },
          ]}
        />
      );
    }

    return content;
  }

  return (
    <CardContainer>
      {isLoadingInitialData ? <CenterSpin /> : <XenoPrevContent />}
    </CardContainer>
  );
}
